import { Link } from 'react-router-dom';
import { ArrowUpRight, Facebook, Instagram, Mail, MapPin, Phone, Star } from 'lucide-react';
import Logo from './Logo';
import {
  ADDRESS_LINE_1,
  ADDRESS_LINE_2,
  EMAIL,
  FACEBOOK_URL,
  GOOGLE_REVIEW_URL,
  HOURS,
  INSTAGRAM_URL,
  JANE_URL,
  MAPS_URL,
  NAV_LINKS,
  PHONE,
  PHONE_HREF,
} from '../data/site';

export default function Footer() {
  return (
    <footer className="bg-brand-ink text-brand-cream" data-testid="site-footer">
      <div className="mx-auto max-w-7xl px-5 pb-10 pt-20 md:px-10 md:pt-28">
        <a
          data-testid="footer-book-link"
          href={JANE_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="group flex flex-wrap items-end justify-between gap-6 border-b border-brand-cream/10 pb-12"
        >
          <span className="font-display text-[clamp(3rem,9vw,6.5rem)] font-light leading-[0.95] tracking-tight transition-colors duration-500 group-hover:text-brand-sage">
            Book a <span className="italic">Massage</span>
          </span>
          <span className="mb-2 inline-flex size-14 items-center justify-center rounded-full border border-brand-cream/25 transition-[background-color,transform] duration-500 group-hover:rotate-45 group-hover:bg-brand-teal md:size-20">
            <ArrowUpRight className="size-6 md:size-9" />
          </span>
        </a>

        <div className="grid gap-12 py-14 md:grid-cols-12">
          <div className="md:col-span-4">
            <Logo dark />
            <p className="mt-6 max-w-xs text-sm leading-relaxed text-brand-cream/60">
              Therapeutic, prenatal, and medical massage in Humble, Texas — a space where every body
              can finally exhale.
            </p>
            <p className="mt-6 text-xs uppercase tracking-[0.25em] text-brand-cream/40">
              Healing through hands
            </p>
            <div className="mt-6 flex items-center gap-3" data-testid="footer-social">
              <a
                data-testid="footer-instagram"
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Thula Doula Wellness on Instagram"
                className="inline-flex size-10 items-center justify-center rounded-full border border-brand-cream/25 text-brand-cream/80 transition-[color,border-color,background-color] duration-300 hover:border-brand-cream hover:bg-brand-cream hover:text-brand-ink"
              >
                <Instagram className="size-4" />
              </a>
              <a
                data-testid="footer-facebook"
                href={FACEBOOK_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Thula Doula Wellness on Facebook"
                className="inline-flex size-10 items-center justify-center rounded-full border border-brand-cream/25 text-brand-cream/80 transition-[color,border-color,background-color] duration-300 hover:border-brand-cream hover:bg-brand-cream hover:text-brand-ink"
              >
                <Facebook className="size-4" />
              </a>
              <a
                data-testid="footer-google-review"
                href={GOOGLE_REVIEW_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full border border-brand-cream/25 px-4 py-2 text-xs font-semibold text-brand-cream/80 transition-[color,border-color,background-color] duration-300 hover:border-brand-cream hover:bg-brand-cream hover:text-brand-ink"
              >
                <Star className="size-3.5 text-brand-sage" />
                Review us on Google
              </a>
              <span className="text-xs text-brand-cream/50">Follow along @thuladoulamassagehtx</span>
            </div>
          </div>

          <div className="md:col-span-3" data-testid="footer-visit">
            <h3 className="text-xs font-semibold uppercase tracking-[0.25em] text-brand-sage">
              Visit
            </h3>
            <a
              data-testid="footer-maps-link"
              href={MAPS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-5 flex items-start gap-3 text-sm leading-relaxed text-brand-cream/75 transition-colors duration-300 hover:text-brand-cream"
            >
              <MapPin className="mt-0.5 size-4 shrink-0 text-brand-sage/70" />
              <span>
                {ADDRESS_LINE_1}
                <br />
                {ADDRESS_LINE_2}
              </span>
            </a>
            <a
              data-testid="footer-phone-link"
              href={PHONE_HREF}
              className="mt-4 flex items-center gap-3 text-sm text-brand-cream/75 transition-colors duration-300 hover:text-brand-cream"
            >
              <Phone className="size-4 text-brand-sage/70" /> {PHONE}
            </a>
            <a
              data-testid="footer-email-link"
              href={`mailto:${EMAIL}`}
              className="mt-4 flex items-center gap-3 break-all text-sm text-brand-cream/75 transition-colors duration-300 hover:text-brand-cream"
            >
              <Mail className="size-4 text-brand-sage/70" /> {EMAIL}
            </a>
          </div>

          <div className="md:col-span-2">
            <h3 className="text-xs font-semibold uppercase tracking-[0.25em] text-brand-sage">
              Explore
            </h3>
            <ul className="mt-5 space-y-3">
              {NAV_LINKS.map((l) => (
                <li key={l.path}>
                  <Link
                    to={l.path}
                    data-testid={`footer-link-${l.path.replace('/', '') || 'home'}`}
                    className="text-sm text-brand-cream/75 transition-colors duration-300 hover:text-brand-cream"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
              <li>
                <Link
                  to="/policies"
                  data-testid="footer-link-policies"
                  className="text-sm text-brand-cream/75 transition-colors duration-300 hover:text-brand-cream"
                >
                  Policies
                </Link>
              </li>
              <li>
                <Link
                  to="/privacy"
                  data-testid="footer-link-privacy"
                  className="text-sm text-brand-cream/75 transition-colors duration-300 hover:text-brand-cream"
                >
                  Privacy Policy
                </Link>
              </li>
            </ul>
          </div>

          <div className="md:col-span-3">
            <h3 className="text-xs font-semibold uppercase tracking-[0.25em] text-brand-sage">
              Hours
            </h3>
            <ul className="mt-5 space-y-1.5" data-testid="footer-hours">
              {HOURS.map((h) => (
                <li key={h.day} className="flex justify-between gap-6 text-sm text-brand-cream/75">
                  <span>{h.day}</span>
                  <span>{h.time}</span>
                </li>
              ))}
              <li className="pt-1 text-sm text-brand-cream/50">Saturday & Sunday · Closed</li>
            </ul>
            <p className="mt-4 text-sm leading-relaxed text-brand-cream/60">
              Live availability is always visible on our Jane booking page.
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-3 border-t border-brand-cream/10 pt-8 text-xs text-brand-cream/40 md:flex-row md:items-center md:justify-between">
          <p>© {new Date().getFullYear()} Thula Doula Wellness. All rights reserved.</p>
          <div className="flex items-center gap-6">
            <Link
              to="/policies"
              data-testid="footer-policies-bottom"
              className="transition-colors duration-300 hover:text-brand-cream"
            >
              Policies
            </Link>
            <Link
              to="/privacy"
              data-testid="footer-privacy-bottom"
              className="transition-colors duration-300 hover:text-brand-cream"
            >
              Privacy Policy
            </Link>
            <p data-testid="footer-license" className="transition-colors duration-300 hover:text-brand-cream/60">
              Licensed Massage Establishment · Texas Lic. No. ME10742 · Humble, Texas
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
